import type { ViewSettingEntry } from "./entry"

/**
 * Where a view's saved settings are kept. The view composables decide WHEN to
 * read and write (see useViewSettings); an app decides WHERE, by registering one
 * adapter at startup.
 *
 * The first three are synchronous and hold the settings for this session: a
 * view reads its settings while it is being set up, so there is nothing to
 * await. `write` and `erase` send a change to wherever it is kept beyond this
 * session; a host that keeps nothing beyond the session leaves them out.
 */
export interface ViewSettingsAdapter {
  /** The stored entry for `key`, or undefined. */
  get(key: string): ViewSettingEntry | undefined
  /** Record an entry at once, so `get` returns it from now on. */
  set(key: string, entry: ViewSettingEntry): void
  /** Forget a key at once. */
  remove(key: string): void
  /** Send an entry to the backing store. Rejects when the write fails. */
  write?(key: string, entry: ViewSettingEntry): Promise<void>
  /** Delete a key from the backing store. Rejects when the delete fails. */
  erase?(key: string): Promise<void>
}

/** Settings kept in memory for the life of the page, with no backing store. */
export function createMemoryViewSettingsAdapter(): ViewSettingsAdapter {
  const entries = new Map<string, ViewSettingEntry>()
  return {
    get: (key) => entries.get(key),
    set: (key, entry) => {
      entries.set(key, entry)
    },
    remove: (key) => {
      entries.delete(key)
    },
  }
}

// One adapter per app, registered at startup (the way core's registries are).
// Until an app registers one, settings live in memory, which is what a test or
// a view with nowhere to save wants.
let current: ViewSettingsAdapter = createMemoryViewSettingsAdapter()

/** Set the adapter every view composable uses. Call once, at startup. */
export function registerViewSettingsAdapter(adapter: ViewSettingsAdapter): void {
  current = adapter
}

/** The registered adapter. */
export function getViewSettingsAdapter(): ViewSettingsAdapter {
  return current
}
